import type { Metric } from 'web-vitals'

type WebVitalsMetric = {
  name: string
  id: string
  rating: 'good' | 'needs-improvement' | 'poor'
  delta: number
}

type SendOptions = {
  page_path?: string
  navigation_type?: 'hard' | 'spa'
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
  }
}

const RELEASE_HASH =
  (process.env.GATSBY_GIT_COMMIT_HASH || '').toString().slice(0, 8) || 'unknown'

const ROUTE_LCP_TIMEOUT_MS = 10_000
const LCP_THRESHOLDS = [2500, 4000]

let currentPagePath = ''
let started = false
let isFirstRoute = true
let hasClientNavigation = false

type RouteLCPSession = {
  pagePath: string
  routeStart: number
  latestEntry: PerformanceEntry | null
  observer: PerformanceObserver | null
  timeoutId: ReturnType<typeof setTimeout> | null
  reported: boolean
}

let routeLCPSession: RouteLCPSession | null = null

function getRating(
  value: number,
  thresholds: number[],
): WebVitalsMetric['rating'] {
  if (value > thresholds[1]) return 'poor'
  if (value > thresholds[0]) return 'needs-improvement'
  return 'good'
}

function sendToGa4(metric: WebVitalsMetric, opts?: SendOptions): void {
  if (typeof window === 'undefined') return
  if (typeof window.gtag !== 'function') return

  const isCls = metric.name === 'CLS'
  const metricValue = isCls
    ? Math.round(metric.delta * 1000)
    : Math.round(metric.delta)

  window.gtag('event', `web_vital_${metric.name.toLowerCase()}`, {
    metric_value: metricValue,
    metric_rating: metric.rating,
    metric_id: metric.id,
    release_hash: RELEASE_HASH,
    page_path: opts?.page_path ?? currentPagePath,
    ...(opts?.navigation_type ? { navigation_type: opts.navigation_type } : {}),
  })
}

function cleanupRouteLCPSession(session: RouteLCPSession): void {
  session.observer?.disconnect()
  if (session.timeoutId) clearTimeout(session.timeoutId)
}

function flushRouteLCP(): void {
  const session = routeLCPSession
  if (!session || session.reported) return

  session.reported = true
  cleanupRouteLCPSession(session)
  routeLCPSession = null

  if (!session.latestEntry) return

  const value = session.latestEntry.startTime - session.routeStart
  if (value < 0) return

  sendToGa4(
    {
      name: 'LCP',
      id: `route-lcp-${session.routeStart}`,
      rating: getRating(value, LCP_THRESHOLDS),
      delta: value,
    },
    { page_path: session.pagePath, navigation_type: 'spa' },
  )
}

function startRouteLCPTracking(pagePath: string): void {
  const routeStart = performance.now()

  const session: RouteLCPSession = {
    pagePath,
    routeStart,
    latestEntry: null,
    observer: null,
    timeoutId: null,
    reported: false,
  }

  try {
    if (
      typeof PerformanceObserver !== 'undefined' &&
      PerformanceObserver.supportedEntryTypes?.includes('largest-contentful-paint')
    ) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.startTime >= routeStart) {
            session.latestEntry = entry
          }
        }
      })
      observer.observe({ type: 'largest-contentful-paint', buffered: false })
      session.observer = observer
    }
  } catch {
    // Unsupported browser.
  }

  session.timeoutId = setTimeout(flushRouteLCP, ROUTE_LCP_TIMEOUT_MS)
  routeLCPSession = session
}

export function trackRouteChange(
  pagePath: string,
  { isClientNavigation = false }: { isClientNavigation?: boolean } = {},
): void {
  if (isClientNavigation) {
    hasClientNavigation = true
  }

  flushRouteLCP()
  currentPagePath = pagePath

  if (isFirstRoute) {
    isFirstRoute = false
    return
  }

  startRouteLCPTracking(pagePath)
}

export async function initWebVitals(): Promise<void> {
  if (started) return
  started = true

  const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import('web-vitals')

  const handler = (metric: Metric) => {
    const isHardNavigationMetric =
      metric.name === 'FCP' || metric.name === 'TTFB'

    sendToGa4(metric, {
      navigation_type:
        isHardNavigationMetric || !hasClientNavigation ? 'hard' : 'spa',
    })
  }

  onLCP(handler, { reportAllChanges: true })
  onINP(handler, { reportAllChanges: true })
  onCLS(handler, { reportAllChanges: true })
  onFCP(handler)
  onTTFB(handler)
}
